import { Buffer } from 'buffer';
// TON/Solana libs expect Node Buffer global. Provide polyfill before imports run.
(window as any).Buffer ??= Buffer;

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { persistedProduct } from '@/product';

// TACITA design tokens and base styles (docs/cabinet-redesign/03_ДИЗАЙН_СИСТЕМА.md). Vuetify is gone: no screen used it.
import './styles/index.scss';

import App from './App.vue';
import { router } from './router';
import { i18n, detectInitialLocale, type Locale } from './i18n';
import { keepServerLanguageInSync } from './i18n/serverLanguage';
import { useAuthStore } from './stores/auth';
import { startAppUpdateChecks } from './appUpdate/useAppUpdate';
import { appHeight, isIos } from './appHeight';
import { rememberHandoffToken } from '@/magicHandoff';



const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// Every product boots in the Tacita theme (index.html sets it statically; App.vue keeps it). The old cold-boot switch
// to `light` painted one frame in a theme no screen uses.
document.documentElement.setAttribute('data-theme', 'tacita');
const bootProduct = persistedProduct();

// Мобильная высота вьюпорта — нижнее меню должно быть закреплено с первого кадра.
// В обычной вкладке берём window.innerHeight: это исключает видимый URL-bar. В standalone PWA
// пиксельное innerHeight использовать нельзя: новые версии iOS могут вернуть высоту рабочей области
// без нижнего системного inset. В результате shell заканчивается выше экрана и при resize/input
// начинает прыгать. Для установленного приложения 100dvh — источник истины; viewport-fit=cover и
// padding с env(safe-area-inset-*) уже отдельно защищают интерактивные элементы.
const standalonePwa = window.matchMedia('(display-mode: standalone)').matches
  || ('standalone' in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true);

/** Height of the window the page is drawn into, as CSS lays it out; 0 if it cannot be measured yet. */
function fixedBoxHeight(): number {
  if (!document.body) return 0;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;bottom:0;left:0;width:0;visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return Math.round(height);
}

function syncAppHeight() {
  // Keep the custom property as an absolute length: affected iOS standalone builds can resolve a custom
  // property containing `100dvh` to zero during cold boot. Which height is right differs between iOS and
  // Android in an installed app — see appHeight.ts.
  const height = appHeight({
    standalone: standalonePwa,
    ios: isIos(navigator),
    innerHeight: window.innerHeight,
    fixedBoxHeight: fixedBoxHeight(),
  });
  document.documentElement.style.setProperty('--app-height', `${height}px`);
}
syncAppHeight();
// An iOS standalone viewport follows dvh itself. Listening to every iOS resize there reintroduced a
// one-frame layout jump while entering PIN/OTP even though no JS update was necessary. An installed Android app
// takes innerHeight like a tab (appHeight.ts) and must follow it the same way: with the keyboard up the window
// shrinks, and a shell kept at the full height left a task chat's composer under the keyboard.
if (!standalonePwa || !isIos(navigator)) window.addEventListener('resize', syncAppHeight);
window.addEventListener('orientationchange', syncAppHeight);

// Create the app + install pinia FIRST so persisted stores (auth.customerId) hydrate. The router is
// installed later (after initializeAuth) so its initial navigation sees the restored auth state.
const app = createApp(App).use(pinia).use(i18n);

// Sync persisted preferredLanguage with i18n on bootstrap (pinia attached → store hydrated).
const auth = useAuthStore();
if (auth.preferredLanguage && auth.preferredLanguage !== i18n.global.locale.value) {
  i18n.global.locale.value = auth.preferredLanguage;
  document.documentElement.setAttribute('lang', auth.preferredLanguage);
} else if (bootProduct === 'vitrine' && !auth.preferredLanguage) {
  // Первый приход (обычно по инвайт-ссылке): своего выбора в сессии ещё нет — берём его из браузера.
  // Клиентура разноязычная, фиксированного языка здесь быть не должно. Дальше клиент правит в Настройках,
  // оператор — в БО.
  const detected: Locale = detectInitialLocale();
  i18n.global.locale.value = detected;
  document.documentElement.setAttribute('lang', detected);
}
// The server renders the statement, e-mails and pushes in the client's preferred language, so it must hold the
// language the cabinet shows — pushed on every vitrine sign-in (boot restore, magic link, OTP login).
keepServerLanguageInSync(auth, () => i18n.global.locale.value as Locale);

// Access token is in-memory only (refresh lives in an HttpOnly cookie). On cold load, restore the
// session via a silent refresh from the cookie BEFORE the app/router is created, so the router's very
// first navigation sees the correct auth state. No-op for anonymous users (no persisted session hint).
void (async () => {
  // Magic-link boot hand-off: if the URL carries a ?token (the /magic link, or /card?token=… opened in a
  // real browser after an in-app browser), exchange it for a session BEFORE the router navigates. The token
  // is reusable for a short window server-side, so opening the same URL in another browser (in-app → Safari)
  // logs in there too — each browser keeps its own cookie. The token stays in the URL for that hand-off.
  const magicToken = new URLSearchParams(window.location.search).get('token');
  if (magicToken && !auth.isAuthenticated) {
    try {
      const { vitrineAuthApi } = await import('@/api/vitrineAuth');
      const r = await vitrineAuthApi.loginMagic(magicToken.trim());
      if (r && !(r as { error?: string }).error) {
        auth.setTokens(r);
        rememberHandoffToken(magicToken.trim());
      }
    } catch { /* fall through — initializeAuth / the router handle the anonymous case */ }
  }
  await auth.initializeAuth();
  // The language on screen reaches the server from keepServerLanguageInSync (above) — on this boot and on every
  // later sign-in, not only when it was guessed from the browser.
  app.use(router);   // installing the router triggers the initial navigation — do it AFTER auth is ready
  app.mount('#app');
})();

// An open app must not keep running code older than the deployed API (prod 28.09): watch for new builds.
// Started independently of the boot above, so even a build that fails to boot can still move onto a fix.
startAppUpdateChecks();
